import { useEffect } from 'react'

interface SEOHeadProps {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  ogImage?: string
  ogType?: 'website' | 'article'
  publishedTime?: string
  modifiedTime?: string
  author?: string
  noIndex?: boolean
  structuredData?: Record<string, unknown>
}

export const SEOHead = ({
  title = 'Party Guestbook - Capture Every Moment, Create Lasting Memories',
  description = 'Transform the way you remember special occasions. Create digital guestbooks for weddings, birthdays, graduations, and more with QR code access.',
  keywords = 'digital guestbook, party guestbook, wedding guestbook, QR code guestbook, event memories, photo sharing, guest messages, celebration memories, funeral guestbook, memorial guestbook',
  canonical,
  ogImage = 'https://partyguestbook.com/og-image.jpg',
  ogType = 'website',
  publishedTime,
  modifiedTime,
  author,
  noIndex = false,
  structuredData,
}: SEOHeadProps) => {
  useEffect(() => {
    // Update document title
    document.title = title

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name'
      let meta = document.querySelector(
        `meta[${attribute}="${name}"]`,
      ) as HTMLMetaElement

      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attribute, name)
        document.head.appendChild(meta)
      }

      meta.setAttribute('content', content)
    }

    // Update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement

      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', rel)
        document.head.appendChild(link)
      }

      link.setAttribute('href', href)
    }

    // Basic meta tags
    updateMetaTag('description', description)
    updateMetaTag('keywords', keywords)
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow')

    // Open Graph tags
    updateMetaTag('og:title', title, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:type', ogType, true)
    updateMetaTag('og:image', ogImage, true)
    updateMetaTag('og:image:alt', title, true)
    updateMetaTag('og:site_name', 'Party Guestbook', true)
    updateMetaTag('og:locale', 'en_US', true)

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', title)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:image', ogImage)
    updateMetaTag('twitter:image:alt', title)

    // Article specific tags
    if (ogType === 'article') {
      if (publishedTime) {
        updateMetaTag('article:published_time', publishedTime, true)
      }
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime, true)
      }
      if (author) {
        updateMetaTag('article:author', author, true)
      }
    }

    // Canonical URL
    if (canonical) {
      updateLinkTag('canonical', canonical)
    }

    // Update URL for Open Graph
    if (canonical) {
      updateMetaTag('og:url', canonical, true)
    }

    // Structured Data
    if (structuredData) {
      // Remove existing structured data
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]#dynamic-seo',
      )
      if (existingScript) {
        existingScript.remove()
      }

      // Add new structured data
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = 'dynamic-seo'
      script.textContent = JSON.stringify(structuredData)
      document.head.appendChild(script)
    }
  }, [
    title,
    description,
    keywords,
    canonical,
    ogImage,
    ogType,
    publishedTime,
    modifiedTime,
    author,
    noIndex,
    structuredData,
  ])

  return null
}
